// src/components/dashboard/adimplencias/contratantes/formulario.js
import React, { useMemo, useRef, useState, useEffect } from 'react';
import { toast } from 'react-toastify';

import { Button } from 'primereact/button';
import { Calendar } from 'primereact/calendar';
import { Dialog } from 'primereact/dialog';
import { Dropdown } from 'primereact/dropdown';
import { InputMask } from 'primereact/inputmask';
import { InputText } from 'primereact/inputtext';
import { classNames } from 'primereact/utils';

import { addHours } from 'date-fns';
import { format } from 'date-fns';
import { mask, unMask } from 'remask';

import { consultaCEP, validaCnpj, validaCpf } from '../../../../utils/validacoes.js';

import { addLocale, locale } from 'primereact/api';
import { FaCalendarAlt, FaPlus } from 'react-icons/fa';
import { Api, ApiAdimplencia } from '../../../../services/api';
import { CalendarPague, DropdownPague } from '../../../../styles/primeReactConfig.js';
import AppCard from '../../../appCard.js';
import useTerminologiaRecorrencia from '../../../../hooks/useTerminologiaRecorrencia.js';

/**
 * Configura locale pt-BR globalmente (executa 1x no load do módulo).
 */
addLocale('pt-BR', {
  firstDayOfWeek: 1,
  dayNames: ['domingo', 'segunda-feira', 'terça-feira', 'quarta-feira', 'quinta-feira', 'sexta-feira', 'sábado'],
  dayNamesShort: ['dom', 'seg', 'ter', 'qua', 'qui', 'sex', 'sáb'],
  dayNamesMin: ['D', 'S', 'T', 'Q', 'Q', 'S', 'S'],
  monthNames: ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'],
  monthNamesShort: ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'],
  today: 'Hoje',
  clear: 'Claro',
});
locale('pt-BR');

/**
 * Normaliza o valor de credor_grupo_id para envio à API: sempre string UUID ou null.
 * Evita enviar o objeto inteiro do dropdown (ex.: { label, value }).
 *
 * @param {string|null|undefined|{ value?: string|null }|*} raw - Valor do estado/formulário (pode vir como objeto do dropdown).
 * @returns {string|null} UUID em string ou null.
 */
function normalizarCredorGrupoId(raw) {
  if (raw === null || raw === undefined) {
    return null;
  }
  if (typeof raw === 'object' && raw !== null && 'value' in raw) {
    const v = raw.value;
    if (v === null || v === undefined || (typeof v === 'string' && v.trim() === '')) {
      return null;
    }
    return typeof v === 'string' ? v : null;
  }
  if (typeof raw === 'string') {
    return raw.trim() === '' ? null : raw;
  }
  return null;
}

/**
 * Remove caracteres inválidos para campos "Nome" (permite espaço).
 * - Não faz trim aqui para não “matar” o espaço durante digitação.
 *
 * @param {string} value
 * @returns {string}
 */
function sanitizeNomeLike(value) {
  if (!value) return '';
  // remove números e pontuações comuns (mantém letras/acentos e espaços)
  const cleaned = value.replace(/[0-9!@#$%^&()_={}:;,.<>+?]/g, '');
  // normaliza múltiplos espaços
  return cleaned.replace(/\s{2,}/g, ' ').toUpperCase();
}

/**
 * Formata telefone BR conforme o usuário digita.
 * Limita em 11 dígitos (DDD + 9).
 *
 * @param {string} value
 * @returns {string}
 */
function formatTelefoneBR(value) {
  const digits = unMask(value || '').slice(0, 11);
  return mask(digits, ['(99) 9999-9999', '(99) 99999-9999']);
}

/**
 * Parse de data BR a partir do input (aceita "dd/mm/aaaa" ou "ddmmaaaa").
 *
 * @param {string} input
 * @returns {Date|null}
 */
function parseDateBRFromInput(input) {
  const digits = String(input || '').replace(/\D/g, '');
  if (digits.length !== 8) return null;

  const day = Number(digits.slice(0, 2));
  const month = Number(digits.slice(2, 4));
  const year = Number(digits.slice(4, 8));

  if (!day || !month || !year) return null;

  const date = new Date(year, month - 1, day);

  // valida data real (ex.: 31/02 não passa)
  if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) {
    return null;
  }

  return date;
}

/**
 * Retorna o tipo do documento quando completo.
 * - CPF:  11 dígitos
 * - CNPJ: 14 dígitos
 *
 * @param {string} maskedDoc
 * @returns {'CPF'|'CNPJ'|null}
 */
function getDocumentoTipo(maskedDoc) {
  const digits = unMask(maskedDoc || '');
  if (digits.length === 11) return 'CPF';
  if (digits.length === 14) return 'CNPJ';
  return null;
}

/**
 * Retorna um "hint" de tipo conforme o usuário digita:
 * - 1..11 dígitos => assume CPF
 * - 12..14 dígitos => assume CNPJ
 *
 * Isso é usado para label/placeholder dinâmicos.
 *
 * @param {string} maskedDoc
 * @returns {'CPF'|'CNPJ'|null}
 */
function getDocumentoTipoHint(maskedDoc) {
  const len = unMask(maskedDoc || '').length;
  if (len <= 0) return null;
  if (len <= 11) return 'CPF';
  return 'CNPJ';
}

/**
 * Valida CPF/CNPJ quando completo (11/14 dígitos).
 * OBS: não valida "parcial".
 *
 * @param {string} maskedDoc
 * @returns {boolean}
 */
function isDocumentoValido(maskedDoc) {
  const tipo = getDocumentoTipo(maskedDoc);
  if (tipo === 'CPF') return validaCpf(maskedDoc);
  if (tipo === 'CNPJ') return validaCnpj(maskedDoc);
  return false;
}

/**
 * @typedef {Object} ContratanteFormProps
 * @property {Object} formData
 * @property {(updater: any) => void} setFormData
 * @property {(e: React.FormEvent<HTMLFormElement>) => void} onSubmit
 * @property {boolean} isLoading
 * @property {boolean} isEditMode
 * @property {Array<{label: string, value: string}>} [gruposOptions]
 * @property {() => Promise<void>} [loadGrupos]
 */

/**
 * Formulário de Contratante (CPF/CNPJ).
 * - Permite digitar CNPJ sem travar (não valida CPF durante digitação).
 * - Valida CNPJ automaticamente ao completar 14 dígitos.
 * - Valida CPF apenas ao sair do campo (onBlur) se estiver com 11 dígitos.
 *
 * @param {ContratanteFormProps} props
 * @returns {JSX.Element}
 */
const ContratanteForm = ({ formData, setFormData, onSubmit, isLoading, isEditMode, gruposOptions = [], loadGrupos }) => {
  const terminologia = useTerminologiaRecorrencia();
  const [isDisabled, setIsDisabled] = useState(false);
  const [showNovoGrupo, setShowNovoGrupo] = useState(false);
  const [novoGrupoDescricao, setNovoGrupoDescricao] = useState('');
  const [salvandoGrupo, setSalvandoGrupo] = useState(false);

  const minNascimento = useMemo(() => new Date('1900-01-01'), []);
  const maxNascimento = useMemo(() => new Date(), []);

  const docTipoHint = useMemo(
    () => getDocumentoTipoHint(formData?.usuario_documento),
    [formData?.usuario_documento]
  );

  const labelDocumento = docTipoHint ? docTipoHint : 'CPF/CNPJ';
  const placeholderDocumento =
    docTipoHint === 'CNPJ'
      ? 'ex: 12.345.678/0001-99'
      : docTipoHint === 'CPF'
        ? 'ex: 999.999.999-99'
        : 'ex: 999.999.999-99 ou 12.345.678/0001-99';

  const labelNome = docTipoHint === 'CNPJ' ? 'Razão Social' : docTipoHint === 'CPF' ? 'Nome' : 'Nome/Razão Social';
  const placeholderNome = docTipoHint === 'CNPJ' ? 'ex: EMPRESA LTDA' : 'ex: José Santos';

  const labelData = docTipoHint === 'CNPJ' ? 'Data de Criação' : docTipoHint === 'CPF' ? 'Data de Nascimento' : 'Data de Nascimento/Criação';
  const placeholderData = docTipoHint === 'CNPJ' ? 'ex: 01/01/2020' : 'ex: 21/06/1992';

  // evita validar/consultar repetidamente o mesmo documento completo
  const lastProcessedDocRef = useRef('');

  /**
   * Recupera dados do usuário pelo CPF/CNPJ (sem máscara).
   *
   * @param {string} documentMasked
   * @returns {Promise<void>}
   */
  const recuperaUsuarioDaPague = async (documentMasked) => {
    // garante que o usuário não fique “preso” em disabled após corrigir
    setIsDisabled(false);

    try {
      const { data } = await Api.get(`users/user/${unMask(documentMasked)}`);
      if (!data) return;

      setFormData((prev) => ({
        ...prev,
        usuario_nome: sanitizeNomeLike(data.usuario_nome || ''),
        usuario_documento: mask(unMask(data.usuario_documento || ''), ['999.999.999-99', '99.999.999/9999-99']),
        usuario_data_nascimento: data.usuario_data_nascimento
          ? addHours(new Date(data.usuario_data_nascimento), 3)
          : prev.usuario_data_nascimento,
        contratante_email: String(data.usuario_email || '').trim().toLowerCase(),
        contratante_telefone: formatTelefoneBR(data.usuario_telefone || ''),
        endereco_cep: mask(unMask(data.enderecos?.[0]?.endereco_cep || ''), '99999-999'),
        endereco_estado: String(data.enderecos?.[0]?.endereco_estado || '').toUpperCase(),
        endereco_cidade: data.enderecos?.[0]?.endereco_cidade || '',
        endereco_rua: data.enderecos?.[0]?.endereco_rua || '',
        endereco_bairro: data.enderecos?.[0]?.endereco_bairro || '',
        endereco_numero: data.enderecos?.[0]?.endereco_numero || '',
        endereco_complemento: data.enderecos?.[0]?.endereco_complemento || '',
      }));

      toast.success('Usuário encontrado');
    } catch (error) {
      // silenciado como já estava
    }
  };

  /**
   * Processa documento completo (CPF/CNPJ).
   * - Se inválido: toast + limpa campo + desabilita submit.
   * - Se válido: consulta na API.
   *
   * @param {string} rawDigits Apenas números (11 ou 14)
   * @param {string} masked Documento mascarado
   * @returns {Promise<void>}
   */
  const processDocumentoCompleto = async (rawDigits, masked) => {
    // evita processar o mesmo doc completo repetidas vezes
    if (lastProcessedDocRef.current === rawDigits) return;
    lastProcessedDocRef.current = rawDigits;

    const ok = isDocumentoValido(masked);

    if (!ok) {
      toast.error('Documento inválido!');
      setFormData((prev) => ({ ...prev, usuario_documento: '' }));
      setIsDisabled(true);
      lastProcessedDocRef.current = '';
      return;
    }

    setIsDisabled(false);
    await recuperaUsuarioDaPague(masked);
  };

  // se o usuário limpou/alterou o doc manualmente, permite novo processamento
  useEffect(() => {
    const rawDigits = unMask(formData?.usuario_documento || '');
    if (rawDigits.length !== 11 && rawDigits.length !== 14) {
      lastProcessedDocRef.current = '';
    }
  }, [formData?.usuario_documento]);

  const handleSalvarNovoGrupo = async () => {
    const desc = String(novoGrupoDescricao || '').trim();
    if (!desc) {
      toast.error(`Informe a descrição de ${terminologia.labelGrupo.toLowerCase()}.`);
      return;
    }
    if (desc.length > 45) {
      toast.error('Descrição deve ter no máximo 45 caracteres.');
      return;
    }
    setSalvandoGrupo(true);
    try {
      const { data } = await ApiAdimplencia.post('/credorgrupo/inserir', {
        grupo_descricao: desc,
        grupo_status: true
      });
      const novoGrupo = data?.dados || data;
      if (novoGrupo?.credor_grupo_id) {
        toast.success('Cadastro realizado com sucesso.');
        setShowNovoGrupo(false);
        setNovoGrupoDescricao('');
        if (loadGrupos) await loadGrupos();
        setFormData((prev) => ({ ...prev, credor_grupo_id: novoGrupo.credor_grupo_id }));
      } else {
        toast.error('Não foi possível concluir o cadastro.');
      }
    } catch (err) {
      toast.error(err?.response?.data?.mensagem || 'Não foi possível concluir o cadastro.');
    } finally {
      setSalvandoGrupo(false);
    }
  };

  const grupoOptionsWithEmpty = useMemo(() => {
    const opts = [...gruposOptions];
    return [{ label: 'Nenhum', value: null }, ...opts];
  }, [gruposOptions]);

  return (
    <form onSubmit={onSubmit}>
      <AppCard className="flex gap-4">
        <div className="mb-6 text-xl">
          <b>Dados Pessoais/Endereço</b>
        </div>

        <div className="flex md:flex-row flex-col overflow-y-scroll max-h-[55vh] gap-2">
          <div className="w-full lg:w-1/2">
            <label htmlFor="cliente">Nº Cliente</label>
            <InputText
              value={formData.contratante_numero}
              onChange={({ target }) =>
                setFormData((prev) => ({ ...prev, contratante_numero: target.value }))
              }
              placeholder="ex: 001"
              id="cliente"
              className="w-full"
            />

            <label htmlFor="documento">{labelDocumento}</label>
            <InputText
              value={formData.usuario_documento}
              onChange={({ target }) => {
                // ao voltar a digitar, sempre desbloqueia
                if (isDisabled) setIsDisabled(false);

                const rawDigits = unMask(target.value).slice(0, 14); // 11 CPF | 14 CNPJ
                const maskedDoc = mask(rawDigits, ['999.999.999-99', '99.999.999/9999-99']);

                setFormData((prev) => ({ ...prev, usuario_documento: maskedDoc }));

                // ✅ NÃO valida com 11 dígitos durante digitação (pode ser início do CNPJ).
                // ✅ Valida CNPJ automaticamente quando completar 14.
                if (rawDigits.length === 14) {
                  void processDocumentoCompleto(rawDigits, maskedDoc);
                } else {
                  // parcial: deixa o usuário digitar
                  lastProcessedDocRef.current = '';
                }
              }}
              onBlur={() => {
                // ✅ Ao sair do campo: se ficou com 11 dígitos, tratamos como CPF e validamos aqui.
                const rawDigits = unMask(formData?.usuario_documento || '').slice(0, 14);
                if (rawDigits.length !== 11 && rawDigits.length !== 14) return;

                const maskedDoc = mask(rawDigits, ['999.999.999-99', '99.999.999/9999-99']);
                void processDocumentoCompleto(rawDigits, maskedDoc);
              }}
              placeholder={placeholderDocumento}
              id="documento"
              className="w-full"
              disabled={isEditMode}
              inputMode="numeric"
              required
            />

            <label htmlFor="name">{labelNome}</label>
            <InputText
              value={formData.usuario_nome}
              onChange={({ target }) =>
                setFormData((prev) => ({
                  ...prev,
                  usuario_nome: sanitizeNomeLike(target.value), // sem trim durante digitação
                }))
              }
              onBlur={({ target }) =>
                setFormData((prev) => ({
                  ...prev,
                  usuario_nome: sanitizeNomeLike(target.value).trim(), // trim só ao sair
                }))
              }
              placeholder={placeholderNome}
              id="name"
              className="w-full"
              disabled={isEditMode}
              required
            />

            <label htmlFor="nascimento">{labelData}</label>
            <Calendar
              className="formularioAdimplencia"
              id="nascimento"
              locale="pt-BR"
              dateFormat="dd/mm/yy"
              placeholder={placeholderData}
              yearRange="1900:2030"
              pt={CalendarPague}
              showOnFocus={false}
              minDate={minNascimento}
              maxDate={maxNascimento}
              value={formData.usuario_data_nascimento || null}
              onChange={(e) => {
                setFormData((prev) => ({ ...prev, usuario_data_nascimento: e.value || null }));
              }}
              mask="99/99/9999"
              onBlur={(e) => {
                const typed = e?.target?.value;

                // se estiver vazio, deixa o required do form impedir submit
                if (!typed) return;

                const parsed = parseDateBRFromInput(typed);
                if (!parsed) {
                  toast.error('Data inválida.');
                  return;
                }

                if (parsed < minNascimento || parsed > maxNascimento) {
                  toast.error('Data inválida.');
                  return;
                }

                setFormData((prev) => ({ ...prev, usuario_data_nascimento: parsed }));
              }}
              yearNavigator
              icon={
                <FaCalendarAlt
                  className={classNames(
                    'px-4 rounded-r-lg',
                    'w-[50px] h-full',
                    'bg-sky-400 text-white hover:bg-sky-500',
                    'dark:bg-sky-500 dark:text-black dark:hover:bg-sky-400'
                  )}
                />
              }
              showIcon
              required // SEMPRE obrigatório (CPF e CNPJ)
            />

            <label htmlFor="email">E-mail</label>
            <InputText
              value={formData.contratante_email}
              onChange={({ target }) =>
                setFormData((prev) => ({
                  ...prev,
                  contratante_email: target.value.trim().toLowerCase(),
                }))
              }
              placeholder="ex: jose123@gmail.com"
              className="w-full"
              id="email"
              required
            />

            <label htmlFor="contato">Contato</label>
            <InputText
              value={formData.contratante_telefone}
              onChange={({ target }) =>
                setFormData((prev) => ({
                  ...prev,
                  contratante_telefone: formatTelefoneBR(target.value),
                }))
              }
              onBlur={() =>
                setFormData((prev) => ({
                  ...prev,
                  contratante_telefone: formatTelefoneBR(prev.contratante_telefone),
                }))
              }
              placeholder="ex: (99) 99999-9999"
              className="w-full"
              id="contato"
              inputMode="numeric"
              maxLength={15}
              required
            />

            <label htmlFor="nameBeneficiario">Beneficiário</label>
            <InputText
              value={formData.beneficiario}
              onChange={({ target }) =>
                setFormData((prev) => ({
                  ...prev,
                  beneficiario: sanitizeNomeLike(target.value), // sem trim durante digitação
                }))
              }
              onBlur={({ target }) =>
                setFormData((prev) => ({
                  ...prev,
                  beneficiario: sanitizeNomeLike(target.value).trim(),
                }))
              }
              placeholder="ex: José Santos"
              className="w-full"
              id="nameBeneficiario"
            />

            <label htmlFor="grupo">{terminologia.labelGrupoNoContratante}</label>
            <div className="flex gap-2 w-full">
              <Dropdown
                id="grupo"
                value={formData.credor_grupo_id ?? null}
                options={grupoOptionsWithEmpty}
                optionLabel="label"
                optionValue="value"
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, credor_grupo_id: e.value ?? null }))
                }
                placeholder={terminologia.placeholderSelecionarGrupo}
                filter
                filterPlaceholder={terminologia.placeholderBuscarGrupo}
                showClear
                className="flex-1"
                pt={{
                  ...DropdownPague,
                  root: ({ props }) => ({
                    className: classNames(
                      'cursor-pointer inline-flex relative select-none w-full',
                      'bg-white border border-gray-400 transition-colors duration-200 ease-in-out rounded-md',
                      'dark:bg-gray-900 dark:border-blue-900/40 dark:hover:border-blue-300',
                      'hover:border-blue-500 focus:outline-none focus:outline-offset-0'
                    )
                  })
                }}
              />
              {loadGrupos && (
                <Button
                  type="button"
                  icon={<FaPlus className="mr-1" />}
                  label={terminologia.acaoAdicionarGrupo}
                  severity="secondary"
                  onClick={() => setShowNovoGrupo(true)}
                  className="shrink-0"
                />
              )}
            </div>

            {showNovoGrupo && (
              <Dialog
                header={terminologia.tituloCadastrarGrupo}
                visible
                onHide={() => {
                  setShowNovoGrupo(false);
                  setNovoGrupoDescricao('');
                }}
                footer={
                  <div className="flex justify-end gap-2">
                    <Button
                      label="Cancelar"
                      severity="secondary"
                      onClick={() => {
                        setShowNovoGrupo(false);
                        setNovoGrupoDescricao('');
                      }}
                    />
                    <Button
                      label="Salvar"
                      severity="success"
                      loading={salvandoGrupo}
                      onClick={handleSalvarNovoGrupo}
                    />
                  </div>
                }
                pt={{ root: 'w-full max-w-md' }}
              >
                <label htmlFor="novo_grupo_desc">Descrição</label>
                <InputText
                  id="novo_grupo_desc"
                  value={novoGrupoDescricao}
                  onChange={({ target }) => setNovoGrupoDescricao(target.value)}
                  placeholder={terminologia.placeholderExemploGrupo}
                  className="w-full mt-2"
                  maxLength={45}
                />
              </Dialog>
            )}
          </div>

          <div className="w-full lg:w-1/2">
            <label htmlFor="cep">CEP</label>
            <InputMask
              value={formData.endereco_cep}
              onChange={({ target }) =>
                setFormData((prev) => ({ ...prev, endereco_cep: target.value }))
              }
              onComplete={({ value }) => consultaCEP(value, setFormData)}
              placeholder="ex: 99999-999"
              className="w-full"
              id="cep"
              required
              mask="99999-999"
            />

            <label htmlFor="estado">Estado</label>
            <InputMask
              value={formData.endereco_estado}
              onChange={({ target }) =>
                setFormData((prev) => ({ ...prev, endereco_estado: target.value.toUpperCase() }))
              }
              id="estado"
              placeholder="ex: PA"
              className="w-full"
              required
              type="text"
              mask="aa"
            />

            <label htmlFor="cidade">Cidade</label>
            <InputText
              value={formData.endereco_cidade}
              onChange={({ target }) =>
                setFormData((prev) => ({ ...prev, endereco_cidade: target.value }))
              }
              placeholder="ex: Belém"
              className="w-full"
              id="cidade"
              required
            />

            <label htmlFor="rua">Rua</label>
            <InputText
              value={formData.endereco_rua}
              onChange={({ target }) =>
                setFormData((prev) => ({ ...prev, endereco_rua: target.value }))
              }
              placeholder="ex: Av Almirante Barroso"
              className="w-full"
              id="rua"
              required
            />

            <label htmlFor="bairro">Bairro</label>
            <InputText
              value={formData.endereco_bairro}
              onChange={({ target }) =>
                setFormData((prev) => ({
                  ...prev,
                  endereco_bairro: target.value.replace(/[0-9!@#$%^&()_={}:;,.<>+?]/g, ''),
                }))
              }
              placeholder="ex: Souza"
              className="w-full"
              id="bairro"
              required
            />

            <label htmlFor="numero">Número</label>
            <InputText
              value={formData.endereco_numero}
              onChange={({ target }) =>
                setFormData((prev) => ({ ...prev, endereco_numero: target.value }))
              }
              placeholder="ex: 981"
              className="w-full"
              id="numero"
              required
            />

            <label htmlFor="complemento">Complemento</label>
            <InputText
              value={formData.endereco_complemento}
              onChange={({ target }) =>
                setFormData((prev) => ({ ...prev, endereco_complemento: target.value }))
              }
              placeholder="ex: em frente a Lotérica"
              className="w-full"
              id="complemento"
            />
          </div>
        </div>

        <div className="w-full flex items-center justify-center">
          <div className="w-full md:w-2/3 lg:w-1/3">
            <Button
              loading={isLoading}
              className="w-full"
              severity="success"
              label="Enviar"
              type="submit"
              disabled={isDisabled}
            />
          </div>
        </div>
      </AppCard>
    </form>
  );
};

export default ContratanteForm;

/**
 * Prepara os dados do formulário de contratante para envio à API.
 * Remove máscaras e formata os campos obrigatórios.
 *
 * @param {Object} formData
 * @returns {Object}
 */
export function getPayloadTratado(formData) {
  const {
    usuario_id,
    contratante_id,
    endereco_id,
    credor_grupo_id,
    usuario_nome,
    usuario_documento,
    usuario_data_nascimento,
    contratante_email,
    contratante_telefone,
    beneficiario,
    contratante_numero,
    endereco_rua,
    endereco_numero,
    endereco_complemento,
    endereco_bairro,
    endereco_cidade,
    endereco_estado,
    endereco_cep,
  } = formData;

  return {
    ...(usuario_id && { usuario_id }),
    ...(contratante_id && { contratante_id }),
    ...(endereco_id && { endereco_id }),
    credor_grupo_id: normalizarCredorGrupoId(credor_grupo_id),
    usuario_nome: usuario_nome?.trim().toUpperCase(),
    usuario_documento: unMask(usuario_documento),
    usuario_data_nascimento: usuario_data_nascimento ? format(usuario_data_nascimento, 'yyyy-MM-dd') : null,
    contratante_email: contratante_email?.trim().toLowerCase(),
    contratante_telefone: unMask(contratante_telefone),
    beneficiario: beneficiario?.trim().toUpperCase() || null,
    contratante_numero: contratante_numero || null,
    endereco_rua,
    endereco_numero,
    endereco_complemento: endereco_complemento || null,
    endereco_bairro,
    endereco_cidade,
    endereco_estado,
    endereco_cep: unMask(endereco_cep),
  };
}
